import { useState, useRef, useCallback, useEffect, useContext, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { MainAppReducer } from 'Types';
import { LessonReducer } from 'Reducers/lessonReducer';
import { defaultTempoSettings } from 'Reducers/mainAppReducer';
import { sliderToTempo, tempoToSlider } from 'Utils/tempoSliderMapping';
import { NotifyPhaser, NotifyPhaserEvents } from 'Phaser/EventBus2';

export interface TempoContextType {
  //isExpanded: boolean;
  //setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  tempo: number;
  setTempo: React.Dispatch<React.SetStateAction<number>>;
  performanceTempo: number;
  setPerformanceTempo: React.Dispatch<React.SetStateAction<number>>;
  sliderValueTempo: number;
  sliderValueGoal: number;
  setSliderValueTempo: React.Dispatch<React.SetStateAction<number>>;
  setSliderValueGoal: React.Dispatch<React.SetStateAction<number>>;
  //tempPctAdjustment: number;
  //setTempPctAdjustment: React.Dispatch<React.SetStateAction<number>>;
  resetTemp: () => void;
  manualTempoInput: string;
  //setManualTempoInput: React.Dispatch<React.SetStateAction<string>>;
  //setManualTempoChange: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  max_tempo_value: number;
  min_tempo_value: number;
  mapTempoToSlider: (tempo: number, goal: boolean) => void;
  mapSliderToTempo: (sliderValue: number, goal: boolean) => void;
  //tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  //tempoInputRef: React.RefObject<HTMLInputElement | null>;
  //tempoToggleRef: React.RefObject<HTMLDivElement | null>;
  overrideTempo: (tempo: number) => void;
  inAdaptiveSegment: boolean;
  ataEnabled: boolean;
  setAtaEnabled: (enabled: boolean) => void;
  startAdaptiveSegment: (target: number) => void;
  endAdaptiveSegment: (finalTempo: number) => void;
  goalTempo: number;
  setGoalTempo: React.Dispatch<React.SetStateAction<number>>;
  customGoalTempo: number;
  setCustomGoalTempo: (tempo: number) => void;
  usingCustomGoalTempo: boolean;
  setUsingCustomGoalTempo: (using: boolean) => void;
  freePlayMode: boolean;
  setFreePlayMode: (freePlay: boolean) => void;
  effectiveGoalTempo: number;
  abortAdaptiveSegment: () => void;
  changeTempoManually: (value: number) => void;
}

const TempoContext = createContext<TempoContextType | undefined>(undefined);

export const TempoProvider: React.FC<{
  initialGoalTempo: number;
  initialCurrentTempo?: number;
  children: React.ReactNode;
}> = ({ initialGoalTempo, initialCurrentTempo, children }) => {
  const min_tempo_value = 40;
  const max_tempo_value = 300;
  const currentTempoInit = initialCurrentTempo ?? initialGoalTempo;
  const [tempo, setTempo] = useState(currentTempoInit);
  const [performanceTempo, setPerformanceTempo] = useState(72);
  const [sliderValueTempo, setSliderValueTempo] = useState(
    () => tempoToSlider(currentTempoInit, min_tempo_value, performanceTempo, max_tempo_value) * 100,
  );
  const [sliderValueGoal, setSliderValueGoal] = useState(
    () => tempoToSlider(initialGoalTempo, min_tempo_value, performanceTempo, max_tempo_value) * 100,
  );
  const [manualTempoInput, setManualTempoInput] = useState(currentTempoInit.toString());
  const [inAdaptiveSegment, setInAdaptiveSegment] = useState(false);
  const [tempoRestorePoint, setTempoRestorePoint] = useState(initialCurrentTempo ?? 72);
  const [goalTempo, setGoalTempo] = useState(initialGoalTempo);
  const [freePlayMode, setFreePlayMode] = useState(false);
  const [effectiveGoalTempo, setEffectiveGoalTempo] = useState(72);

  const dispatch = useDispatch();

  const startAdaptiveSegment = useCallback(
    (base: number) => {
      setTempoRestorePoint(base);
      setInAdaptiveSegment(true);
    },
    [setInAdaptiveSegment],
  );

  const overrideTempo = useCallback(
    (newTempo: number) => {
      setTempo(newTempo);
      dispatch(appActions.setTempo({ tempo: newTempo, level, tier }));
      setInAdaptiveSegment(false);
    },
    [dispatch],
  );

  const abortAdaptiveSegment = useCallback(() => {
    //NotifyPhaserEvents.AbortAdaptiveTempo({ resetTempo: tempoRestorePoint });
    //overrideTempo(tempoRestorePoint);
    console.debug('abortAdaptiveSegment', tempoRestorePoint);
  }, [tempoRestorePoint, overrideTempo]);

  const endAdaptiveSegment = useCallback(
    (finalTempo: number) => {
      overrideTempo(finalTempo);
      //setInAdaptiveSegment(false);
      //setTempo(finalTempo);
    },
    [overrideTempo],
  );

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  //const tier = useSelector((state: LessonReducer) => state.lessonReducer.tierNumber);
  const tier = useSelector(
    (state: LessonReducer) => state.lessonReducer.currentUserLevelData?.current_tier ?? 1,
  );
  const level = useSelector((state: MainAppReducer) => state.mainAppReducer.levelSelect);

  console.debug('[tier]', tier, 'level', level, 'ataSettings', data.tempoSettings);

  const currentTierAtaSettings = data.tempoSettings?.[level]?.[tier] ?? defaultTempoSettings;

  const prevTierRef = useRef<number | null>(null);

  // Sync context from Redux on mount (rehydrated values) and when tier changes
  useEffect(() => {
    if (prevTierRef.current !== tier) {
      prevTierRef.current = tier;
      const goal = currentTierAtaSettings.goalTempo;
      const current = currentTierAtaSettings.currentTempo;
      setGoalTempo(goal);
      setTempo(current);
      setSliderValueGoal(
        tempoToSlider(goal, min_tempo_value, performanceTempo, max_tempo_value) * 100,
      );
      setSliderValueTempo(
        tempoToSlider(current, min_tempo_value, performanceTempo, max_tempo_value) * 100,
      );
      setManualTempoInput(Math.round(current).toString());
    }
  }, [tier, currentTierAtaSettings, min_tempo_value, max_tempo_value, performanceTempo, level]);

  const ataEnabled = currentTierAtaSettings.ataEnabled;
  const customGoalTempo = currentTierAtaSettings.goalTempo;
  const usingCustomGoalTempo = currentTierAtaSettings.usingCustomGoalTempo;

  const setAtaEnabled = useCallback(
    (enabled: boolean) => {
      dispatch(appActions.setAutoTempoAdjustment({ enabled, tier, level }));
    },
    [dispatch, tier],
  );

  const setCustomGoalTempo = useCallback(
    (tempo: number) => {
      dispatch(appActions.setCustomGoalTempo({ goalTempo: tempo, tier, level }));
    },
    [dispatch, tier],
  );

  const setUsingCustomGoalTempo = useCallback(
    (using: boolean) => {
      dispatch(appActions.setUsingCustomGoalTempo({ usingCustomGoalTempo: using, tier, level }));
    },
    [dispatch, tier],
  );

  const resetTemp = useCallback(() => {
    const base = 72;
    setPerformanceTempo(base);
    setTempo(base);
    //setGoalTempo(base);
    setSliderValueTempo(50);
    mapTempoToSlider(150, true);
    //setSliderValueGoal(50);
    setManualTempoInput(Math.round(base).toString());
  }, []);

  /*const mapTempoToSlider = (newTempo: number) => {
    if (performanceTempo != null) {
      const newTempPctAdjustment = (100 * newTempo) / performanceTempo; // DO not round yet otherwise will not trigger change in tempo (since based on percentage). Only round in display.
      let newValue = ((newTempPctAdjustment - 100) * (100 / 2)) / (2 * 100) + 100 / 2;
      if (newValue <= 50) {
        newValue = ((newTempPctAdjustment - 10) / 90) * (100 / 2);
      }
      setSliderValue(newValue);
      setTempPctAdjustment(newTempPctAdjustment);
    }
  };*/
  const mapTempoToSlider = useCallback(
    (newTempo: number, goal: boolean) => {
      const normalised = tempoToSlider(
        newTempo,
        min_tempo_value,
        performanceTempo,
        max_tempo_value,
      );
      const sliderValue = normalised * 100;
      if (goal) {
        setSliderValueGoal(sliderValue);
        setGoalTempo(newTempo);
      } else {
        setSliderValueTempo(sliderValue);
        setTempo(newTempo);
      }
    },
    [min_tempo_value, max_tempo_value, performanceTempo],
  );

  const mapSliderToTempo = useCallback(
    (sliderValue: number, goal: boolean) => {
      const newTempo = sliderToTempo(
        sliderValue,
        min_tempo_value,
        performanceTempo,
        max_tempo_value,
      );
      //const newTempoPct = (100 * newTempo) / performanceTempo;
      //setTempPctAdjustment(Math.round(newTempoPct));
      if (goal) {
        setGoalTempo(Math.round(newTempo));
        //setSliderValueGoal(sliderValue);
      } else {
        setTempo(Math.round(newTempo));
        //setSliderValueTempo(sliderValue);
      }
      //setTempo(newTempo);
    },
    [min_tempo_value, max_tempo_value, performanceTempo],
  );

  useEffect(() => {
    mapSliderToTempo(sliderValueGoal / 100, true);
  }, [sliderValueGoal]);

  useEffect(() => {
    mapSliderToTempo(sliderValueTempo / 100, false);
  }, [sliderValueTempo]);

  /*useEffect(() => {
    mapTempoToSlider(tempo, false);
  }, [tempo]);

  useEffect(() => {
    mapTempoToSlider(goalTempo, true);
  }, [goalTempo]);*/

  useEffect(() => {
    setEffectiveGoalTempo(freePlayMode && usingCustomGoalTempo ? customGoalTempo : goalTempo);
    NotifyPhaserEvents.UpdateGoalTempo({ goalTempo: effectiveGoalTempo });
  }, [usingCustomGoalTempo, freePlayMode, goalTempo]);

  useEffect(() => {
    setManualTempoInput(Math.round(tempo).toString());
    if (!inAdaptiveSegment) {
      dispatch(appActions.setTempo({ tempo, level, tier }));
    }
  }, [tempo, inAdaptiveSegment]);

  useEffect(() => {
    dispatch(appActions.setCustomGoalTempo({ goalTempo, tier, level }));
  }, [goalTempo]);

  useEffect(() => {
    console.debug('setting tempo', tempo);
    if (!inAdaptiveSegment) dispatch(appActions.setTempo({ tempo, level, tier }));
  }, [tempo, inAdaptiveSegment]);

  useEffect(() => {
    /*if (!inAdaptiveSegment) {
      NotifyPhaser(CancelAdaptiveSegment());
    }*/
  }, [inAdaptiveSegment]);

  // Must be called when manual tempo change is made through UI elements, to abort any adaptive segments
  const changeTempoManually = useCallback(
    (value: number) => {
      NotifyPhaserEvents.AbortAdaptiveTempo({ resetTempo: value });
      overrideTempo(value);
    },
    [overrideTempo],
  );

  const setManualTempoChange = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = Math.round(parseInt(e.target.value, 10));
      if (!isNaN(value)) {
        if (value > max_tempo_value) {
          value = max_tempo_value;
        } else if (value < min_tempo_value) {
          value = min_tempo_value;
        }
        mapTempoToSlider(value, false);
      }
      setManualTempoInput(value.toString());
      changeTempoManually(value);
    },
    [max_tempo_value, min_tempo_value, mapTempoToSlider, changeTempoManually],
  );

  /*useEffect(() => {
    setTempo(Math.round((performanceTempo * tempPctAdjustment) / 100.0));
  }, [performanceTempo, tempPctAdjustment]);*/

  return (
    <TempoContext.Provider
      value={{
        //isExpanded,
        //setIsExpanded,
        tempo,
        setTempo,
        performanceTempo,
        setPerformanceTempo,
        sliderValueTempo,
        sliderValueGoal,
        setSliderValueTempo,
        setSliderValueGoal,
        //setSliderValue,
        //tempPctAdjustment,
        //setTempPctAdjustment,
        resetTemp,
        //setManualTempoChange,
        manualTempoInput,
        //setManualTempoInput,
        max_tempo_value,
        min_tempo_value,
        mapTempoToSlider,
        mapSliderToTempo,
        //tempoContainerRef,
        //tempoInputRef,
        //tempoToggleRef,
        overrideTempo,
        inAdaptiveSegment,
        startAdaptiveSegment,
        endAdaptiveSegment,
        goalTempo,
        setGoalTempo,
        ataEnabled,
        setAtaEnabled,
        customGoalTempo,
        setCustomGoalTempo,
        usingCustomGoalTempo,
        setUsingCustomGoalTempo,
        freePlayMode,
        setFreePlayMode,
        effectiveGoalTempo,
        abortAdaptiveSegment,
        changeTempoManually,
      }}
    >
      {children}
    </TempoContext.Provider>
  );
};

export const useTempoContext = (): TempoContextType => {
  const context = useContext(TempoContext);
  if (!context) {
    throw new Error('useTempoContext must be used within a TempoProvider');
  }
  return context;
};
